/**
 * Cache Manager Utility
 * Centralizes cache clearing for all analysis pages and storage mechanisms
 */

/**
 * All cache keys used in the application
 * Organized by feature/page
 */
const CACHE_KEYS = {
  // Authentication & Owner
  OWNER_DATA: 'owner_data',
  COMPANY_DATA: 'company_data',
  BUSINESS_UNITS: 'business_units',
  USER_TOKEN: 'user_token',
  AUTH_TOKEN: 'auth_token',
  LOGNEX_OWNER: 'lognex_owner',
  USER_DATA: 'user_data',
  COMPANY_INFO: 'company_info',
  AUTH_DATA: 'auth_data',
  LOGNEX_IA_OWNER: 'lognex_ia_owner',

  // Theme & UI
  THEME: 'lognex-theme',
  MODULE_CONFIG: 'lognex-module-config',

  // Analysis Caches - Vehicle Freight
  VEHICLE_FREIGHT_PREFIX: 'vehicle_freight_analysis_cache',

  // Analysis Caches - Freight
  FREIGHT_PREFIX: 'freight_analysis_cache',

  // Analysis Caches - Sales Force
  SALES_FORCE_PREFIX: 'sales_force_analysis_cache',

  // Analysis Caches - NFe
  NFE_PREFIX: 'nfe_ai_analysis_cache',

  // Analysis Caches - CTE
  CTE_PREFIX: 'cte_ai_analysis_cache',
};

/**
 * Clear analysis-specific caches
 * Used when user logs out or switches company
 */
export const clearAnalysisCaches = (): void => {
  try {
    const keysToRemove: string[] = [];

    // Get all localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      // Check if key matches any analysis cache prefix
      if (
        key.startsWith(CACHE_KEYS.VEHICLE_FREIGHT_PREFIX) ||
        key.startsWith(CACHE_KEYS.FREIGHT_PREFIX) ||
        key.startsWith(CACHE_KEYS.SALES_FORCE_PREFIX) ||
        key.startsWith(CACHE_KEYS.NFE_PREFIX) ||
        key.startsWith(CACHE_KEYS.CTE_PREFIX)
      ) {
        keysToRemove.push(key);
      }
    }

    // Remove all analysis caches
    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });

    console.log(`[CacheManager] Cleared ${keysToRemove.length} analysis caches`);
  } catch (error) {
    console.error('[CacheManager] Error clearing analysis caches:', error);
  }
};

/**
 * Clear owner/authentication specific caches
 * Used during logout
 */
export const clearAuthenticationCaches = (): void => {
  try {
    const authKeys = [
      CACHE_KEYS.OWNER_DATA,
      CACHE_KEYS.COMPANY_DATA,
      CACHE_KEYS.BUSINESS_UNITS,
      CACHE_KEYS.USER_TOKEN,
      CACHE_KEYS.AUTH_TOKEN,
      CACHE_KEYS.LOGNEX_OWNER,
      CACHE_KEYS.USER_DATA,
      CACHE_KEYS.COMPANY_INFO,
      CACHE_KEYS.AUTH_DATA,
      CACHE_KEYS.LOGNEX_IA_OWNER,
    ];

    authKeys.forEach((key) => {
      localStorage.removeItem(key);
    });

    console.log('[CacheManager] Cleared authentication caches');
  } catch (error) {
    console.error('[CacheManager] Error clearing authentication caches:', error);
  }
};

/**
 * Clear all UI/preference caches
 * Can be kept after logout if desired (theme preference, module config)
 */
export const clearUICaches = (): void => {
  try {
    localStorage.removeItem(CACHE_KEYS.THEME);
    localStorage.removeItem(CACHE_KEYS.MODULE_CONFIG);
    console.log('[CacheManager] Cleared UI caches');
  } catch (error) {
    console.error('[CacheManager] Error clearing UI caches:', error);
  }
};

/**
 * Clear all caches (comprehensive cleanup)
 * Used during logout
 */
export const clearAllCaches = (): void => {
  try {
    // Clear analysis caches
    clearAnalysisCaches();

    // Clear authentication caches
    clearAuthenticationCaches();

    // Clear UI caches (optional - keeping for user preference)
    // clearUICaches();

    // Clear sessionStorage
    sessionStorage.clear();

    console.log('[CacheManager] All caches cleared successfully');
  } catch (error) {
    console.error('[CacheManager] Error clearing all caches:', error);
  }
};

/**
 * Clear company-specific caches
 * Used when switching companies to ensure old company data is removed
 */
export const clearCompanySpecificCaches = (company?: string): void => {
  try {
    const keysToRemove: string[] = [];

    // Get all localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      // If company is specified, only remove caches for that company
      // Otherwise remove all company-related caches
      if (company) {
        // Remove caches that contain the company name
        if (key.includes(company)) {
          keysToRemove.push(key);
        }
      } else {
        // Remove all analysis caches (they all contain company data)
        if (
          key.startsWith(CACHE_KEYS.VEHICLE_FREIGHT_PREFIX) ||
          key.startsWith(CACHE_KEYS.FREIGHT_PREFIX) ||
          key.startsWith(CACHE_KEYS.SALES_FORCE_PREFIX) ||
          key.startsWith(CACHE_KEYS.NFE_PREFIX) ||
          key.startsWith(CACHE_KEYS.CTE_PREFIX)
        ) {
          keysToRemove.push(key);
        }
      }
    }

    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });

    console.log(
      `[CacheManager] Cleared ${keysToRemove.length} company-specific caches ${
        company ? `for company: ${company}` : ''
      }`
    );
  } catch (error) {
    console.error('[CacheManager] Error clearing company-specific caches:', error);
  }
};

/**
 * Validate that caches are actually cleared
 * Useful for debugging
 */
export const validateCachesClear = (): {
  cleared: boolean;
  remainingCaches: string[];
} => {
  const remainingCaches: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;

    if (
      key.startsWith(CACHE_KEYS.VEHICLE_FREIGHT_PREFIX) ||
      key.startsWith(CACHE_KEYS.FREIGHT_PREFIX) ||
      key.startsWith(CACHE_KEYS.SALES_FORCE_PREFIX) ||
      key.startsWith(CACHE_KEYS.NFE_PREFIX) ||
      key.startsWith(CACHE_KEYS.CTE_PREFIX) ||
      key === CACHE_KEYS.OWNER_DATA
    ) {
      remainingCaches.push(key);
    }
  }

  console.log('[CacheManager] Cache validation:', {
    cleared: remainingCaches.length === 0,
    remainingCaches,
  });

  return {
    cleared: remainingCaches.length === 0,
    remainingCaches,
  };
};

export default {
  clearAnalysisCaches,
  clearAuthenticationCaches,
  clearUICaches,
  clearAllCaches,
  clearCompanySpecificCaches,
  validateCachesClear,
  CACHE_KEYS,
};
